import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Progress } from '@/components/ui/progress';
import { BookOpen, Upload, CheckCircle2, Clock, ChevronRight, Video, ExternalLink, Users, Trophy, FlaskConical } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { useWeeklyProgress, getCurrentWeek, type WeekTask } from '@/hooks/useWeeklyProgress';

interface WeeklyProgressAccordionProps {
  programId: string | undefined;
  cohortStartDate: string | undefined;
  cohortId: string | undefined;
  deadlines?: {
    project_deadline?: string | null;
    project_deadline_week?: number | null;
    challenge_lab_deadline?: string | null;
    challenge_lab_deadline_week?: number | null;
  };
  /** Self-paced route: weeks are suggested, not scheduled */
  selfPaced?: boolean;
}

export function WeeklyProgressAccordion({ programId, cohortStartDate, cohortId, deadlines, selfPaced }: WeeklyProgressAccordionProps) {
  const navigate = useNavigate();
  const { data: weeks = [], isLoading } = useWeeklyProgress(programId, cohortStartDate, cohortId, deadlines);
  const currentWeek = getCurrentWeek(cohortStartDate);

  const suggestedRange = (weekNumber: number) => {
    if (!cohortStartDate) return null;
    const [y, m, d] = cohortStartDate.slice(0, 10).split('-').map(Number);
    const start = new Date(y, (m || 1) - 1, d || 1);
    if (Number.isNaN(start.getTime())) return null;
    start.setDate(start.getDate() + (weekNumber - 1) * 7);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    const fmt = (d: Date) => d.toLocaleDateString('es-CO', { day: '2-digit', month: 'short' });
    return `${fmt(start)} - ${fmt(end)}`;
  };

  const getProgressColor = (progress: number) => {
    if (progress === 100) return 'text-success';
    if (progress >= 50) return 'text-warning';
    return 'text-muted-foreground';
  };

  const getProgressBg = (progress: number) => {
    if (progress === 100) return 'bg-success/10';
    if (progress >= 50) return 'bg-warning/10';
    return 'bg-muted/50';
  };

  const handleTaskClick = (task: WeekTask, weekNumber: number) => {
    if (task.type === 'content') {
      navigate(`/content?week=${weekNumber}&mandatory=1`);
    } else if (task.type === 'live') {
      const isPast = task.endsAt ? new Date(task.endsAt).getTime() < Date.now() : false;
      const url = isPast
        ? task.recordingLink
        : (task.liveLink || task.recordingLink);
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        navigate('/calendar');
      }
    } else if (task.type === 'milestone') {
      navigate(task.milestoneKind === 'challenge_lab' ? '/challenge-lab' : '/challenge');
    } else if (task.refId) {
      navigate(`/calendar?openDeliverable=${task.refId}`);
    } else {
      navigate('/calendar');
    }
  };

  const formatLiveDate = (iso?: string) => {
    if (!iso) return null;
    try {
      const d = new Date(iso);
      return d.toLocaleDateString('es-CO', { weekday: 'short', day: '2-digit', month: 'short' }) +
        ' · ' + d.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return null;
    }
  };

  const formatProjectDeadline = (iso?: string | null) => {
    if (!iso) return null;
    const [datePart] = iso.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    if (!year || !month || !day) return null;
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('es-CO', { day: 'numeric', month: 'long' });
  };

  const projectDeadline = formatProjectDeadline(deadlines?.project_deadline);

  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-16 w-full rounded-xl" />
        ))}
      </div>
    );
  }

  if (weeks.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground text-sm">
        {selfPaced && !cohortStartDate
          ? 'Aún no has iniciado tu plan de estudio.'
          : 'No hay semanas configuradas para esta ruta.'}
      </div>
    );
  }

  return (
    <Accordion type="single" collapsible defaultValue={`week-${currentWeek}`} className="space-y-2">
      {weeks.map((week) => {
        const isCurrentWeek = week.weekNumber === currentWeek;
        const isPastWeek = week.weekNumber < currentWeek;
        const isFutureWeek = week.weekNumber > currentWeek;

        return (
          <AccordionItem
            key={week.weekNumber}
            value={`week-${week.weekNumber}`}
            className={cn(
              "border rounded-xl overflow-hidden transition-all",
              isCurrentWeek && "border-primary/50 bg-primary/5",
              isPastWeek && week.progress === 100 && "border-success/30",
              isFutureWeek && "opacity-60"
            )}
          >
            <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-muted/30 transition-colors">
              <div className="flex items-center justify-between w-full pr-2">
                <div className="flex items-center gap-3">
                  <div className={cn(
                    "w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold",
                    week.progress === 100 ? "bg-success text-success-foreground" :
                    isCurrentWeek ? "bg-primary text-primary-foreground" :
                    "bg-muted text-muted-foreground"
                  )}>
                    {week.progress === 100 ? <CheckCircle2 className="w-5 h-5" /> : week.weekNumber}
                  </div>
                  <div className="text-left">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-foreground">Semana {week.weekNumber}</span>
                      {isCurrentWeek && (
                        <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full font-medium">Actual</span>
                      )}
                      {selfPaced && isFutureWeek && (
                        <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full font-medium">Sugerida</span>
                      )}
                      {suggestedRange(week.weekNumber) && (
                        <span className="text-xs text-muted-foreground">{suggestedRange(week.weekNumber)}</span>
                      )}
                    </div>
                    <span className="text-sm text-muted-foreground">{week.title}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={cn("text-lg font-bold", getProgressColor(week.progress))}>{week.progress}%</span>
                  <div className="w-20">
                    <Progress value={week.progress} className="h-2" variant={week.progress === 100 ? 'success' : 'default'} />
                  </div>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4">
              <div className="space-y-2 pt-2">
                {week.tasks.map((task) => {
                  const isLive = task.type === 'live';
                  const livePast = isLive && task.endsAt ? new Date(task.endsAt).getTime() < Date.now() : false;
                  // Sin fallback cruzado: futura usa solo liveLink (Meet), pasada usa solo
                  // recordingLink. Asi una grabacion mal asignada a sesion futura no la
                  // hace clickeable, y un liveLink en sesion pasada (Meet ya cerrado) tampoco.
                  const liveUrl = isLive
                    ? (livePast ? task.recordingLink : task.liveLink)
                    : undefined;
                  const liveDate = isLive ? formatLiveDate(task.scheduledAt) : null;
                  const liveDisabled = isLive && !liveUrl;

                  return (
                    <button
                      key={task.id}
                      onClick={() => !liveDisabled && handleTaskClick(task, week.weekNumber)}
                      disabled={liveDisabled}
                      className={cn(
                        "w-full flex items-center justify-between p-3 rounded-lg transition-all",
                        isLive
                          ? (liveDisabled ? "bg-muted/30 cursor-not-allowed opacity-70" : "bg-primary/5 hover:bg-primary/10 cursor-pointer hover:scale-[1.01]")
                          : getProgressBg(task.isComplete ? 100 : (task.completed / Math.max(task.total, 1)) * 100),
                        !isLive && "hover:bg-muted/70 cursor-pointer hover:scale-[1.01]"
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          "w-8 h-8 rounded-lg flex items-center justify-center",
                          isLive ? "bg-primary/15" : (task.isComplete ? "bg-success/20" : "bg-muted")
                        )}>
                          {task.type === 'content' ? (
                            <BookOpen className={cn("w-4 h-4", task.isComplete ? "text-success" : "text-muted-foreground")} />
                          ) : task.type === 'deliverable' ? (
                            <Upload className={cn("w-4 h-4", task.isComplete ? "text-success" : "text-muted-foreground")} />
                          ) : task.type === 'milestone' ? (
                            task.milestoneKind === 'challenge_lab' ? (
                              <FlaskConical className={cn("w-4 h-4", task.isComplete ? "text-success" : "text-warning")} />
                            ) : (
                              <Trophy className={cn("w-4 h-4", task.isComplete ? "text-success" : "text-warning")} />
                            )
                          ) : task.liveKind === 'mentoring' ? (
                            <Users className="w-4 h-4 text-primary" />
                          ) : (
                            <Video className="w-4 h-4 text-primary" />
                          )}
                        </div>
                        <div className="text-left">
                          <p className="text-sm font-medium text-foreground">
                            {isLive
                              ? `${task.liveKind === 'mentoring' ? 'Mentoría' : 'Sesión en vivo'}${task.title ? ` · ${task.title}` : ''}`
                              : task.title}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {task.type === 'content' ? (
                              task.isComplete ? (
                                <span className="text-success">Todo el contenido visto</span>
                              ) : (
                                <span>{task.completed} de {task.total} lecciones vistas</span>
                              )
                            ) : task.type === 'deliverable' ? (
                              task.isComplete ? (
                                <span className="text-success">Entregable subido</span>
                              ) : !task.isActive ? (
                                <span className="text-muted-foreground">Próximamente</span>
                              ) : task.availableAfterSession ? (
                                <span className="text-muted-foreground">Disponible tras la sesión en vivo</span>
                              ) : (
                                <span className="text-warning">Pendiente de subir</span>
                              )
                            ) : task.type === 'milestone' ? (
                              <span className="flex flex-col">
                                {task.isComplete ? (
                                  <span className="text-success">
                                    {task.milestoneKind === 'challenge_lab' ? 'Solución registrada' : 'Proyecto entregado'}
                                  </span>
                                ) : !task.isActive ? (
                                  <span className="text-muted-foreground">Próximamente</span>
                                ) : (
                                  <span className="text-warning">
                                    {task.milestoneKind === 'challenge_lab' ? 'Pendiente de completar' : 'Pendiente de entregar'}
                                  </span>
                                )}
                                {task.milestoneKind === 'project' && projectDeadline && (
                                  <span className="text-muted-foreground">Fecha límite: {projectDeadline}</span>
                                )}
                              </span>
                            ) : (
                              <span>
                                {liveDate ?? 'Sin fecha programada'}
                                {livePast && !task.recordingLink && ' · Sesión finalizada'}
                                {liveDisabled && !livePast && ' · Link pendiente'}
                                {!liveDisabled && livePast && task.recordingLink && ' · Ver grabación'}
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {isLive ? (
                          liveDisabled ? (
                            <Clock className="w-5 h-5 text-muted-foreground" />
                          ) : (
                            <span className={cn(
                              "text-xs font-semibold px-2 py-1 rounded-full inline-flex items-center gap-1",
                              livePast && task.recordingLink ? "bg-muted text-foreground" : "bg-primary/20 text-primary"
                            )}>
                              {livePast && task.recordingLink ? 'Grabación' : 'Unirme'}
                              <ExternalLink className="w-3 h-3" />
                            </span>
                          )
                        ) : task.isComplete ? (
                          <CheckCircle2 className="w-5 h-5 text-success" />
                        ) : task.type === 'content' ? (
                          <span className={cn(
                            "text-sm font-medium px-2 py-1 rounded-full",
                            task.completed > 0 ? "bg-warning/20 text-warning" : "bg-muted text-muted-foreground"
                          )}>
                            {task.total > 0 ? Math.round((task.completed / task.total) * 100) : 0}%
                          </span>
                        ) : (
                          <Clock className="w-5 h-5 text-muted-foreground" />
                        )}
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      </div>
                    </button>
                  );
                })}
              </div>
            </AccordionContent>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
}
